import { useTranslation } from 'react-i18next';

import { useMemo, useState } from 'react';

import { Combobox, InputBase, useCombobox } from '@optiro/ui';

import { useCreateTaskCategory } from '@/hooks/useCreateTaskCategory';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { TaskCategory } from '@/types/types';

const CREATE_OPTION_VALUE = '$create';

export interface SelectTaskCategoryProps {
  value: TaskCategory['id'] | null;
  onChange: (value: TaskCategory['id'] | null) => void;
  clearable?: boolean;
}

const CLEAR_OPTION_VALUE = '$clear';

export const SelectTaskCategory = ({ value, onChange, clearable }: SelectTaskCategoryProps) => {
  const { t } = useTranslation('task');
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
    },
  });
  const { data: categories = [] } = useTaskCategories();
  const createCategory = useCreateTaskCategory();
  const [search, setSearch] = useState('');

  const selected = useMemo(
    () => categories.find((c) => c.id === value) ?? null,
    [categories, value]
  );

  const trimmedQuery = search.trim();

  const filtered = useMemo(() => {
    const q = trimmedQuery.toLowerCase();
    if (!q) return categories;
    return categories.filter((c) => c.name.toLowerCase().includes(q));
  }, [categories, trimmedQuery]);

  const hasExactMatch = useMemo(
    () => categories.some((c) => c.name.toLowerCase() === trimmedQuery.toLowerCase()),
    [categories, trimmedQuery]
  );

  const showCreateOption = trimmedQuery.length > 0 && !hasExactMatch;

  const handleSelect = async (submittedValue: string) => {
    if (submittedValue === CREATE_OPTION_VALUE) {
      const created = await createCategory.mutateAsync({ name: trimmedQuery });
      onChange(created.id);
      combobox.closeDropdown();
      return;
    }
    if (submittedValue === CLEAR_OPTION_VALUE) {
      onChange(null);
      setSearch('');
      combobox.closeDropdown();
      return;
    }
    onChange(Number(submittedValue) as TaskCategory['id']);
    combobox.closeDropdown();
  };

  return (
    <Combobox store={combobox} onOptionSubmit={handleSelect}>
      <Combobox.Target>
        <InputBase
          component="input"
          type="text"
          role="textbox"
          value={combobox.dropdownOpened ? search : (selected?.name ?? '')}
          placeholder={t('category.placeholder')}
          rightSection={<Combobox.Chevron />}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          onChange={(event) => {
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
            setSearch(event.currentTarget.value);
          }}
        />
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options>
          {clearable && !trimmedQuery && (
            <Combobox.Option value={CLEAR_OPTION_VALUE} c="dimmed">
              {t('category.none')}
            </Combobox.Option>
          )}
          {filtered.map((c) => (
            <Combobox.Option value={String(c.id)} key={c.id}>
              {c.name}
            </Combobox.Option>
          ))}
          {showCreateOption && (
            <Combobox.Option value={CREATE_OPTION_VALUE}>
              {t('category.create', { name: trimmedQuery })}
            </Combobox.Option>
          )}
          {filtered.length === 0 && !showCreateOption && (
            <Combobox.Empty>{t('category.empty')}</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default SelectTaskCategory;
